/**
 * XRLightProbeSystem — Reads light estimation and updates scene lighting.
 *
 * Phase: RenderSub, SubPhase: PreUpdate, Priority: 15
 * Active only during AR sessions with 'light-estimation' feature granted.
 */

import * as THREE from 'three/webgpu';
import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import type { XRSessionState } from '../types.js';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import { Logger } from '../../core/Logger.js';

export class XRLightProbeSystem implements ISystem {
  readonly name = 'XRLightProbe';
  readonly phase = LoopPhase.RenderSub;
  readonly subPhase = SubPhase.PreUpdate;
  readonly priority = 15;

  private _state: XRSessionState;
  private _scene: THREE.Scene;
  private _probeRequested = false;
  private _probeSession: XRSession | null = null;
  private _warnedMissingFrame = false;

  constructor(state: XRSessionState, scene: THREE.Scene) {
    this._state = state;
    this._scene = scene;
  }

  execute(_dt: number, _world: World): void {
    if (!this._state.isPresenting) return;
    if (!this._state.hasFeature('light-estimation')) return;

    const session = this._state.session!;

    // Reset per-session flag when session changes
    if (session !== this._probeSession) {
      this._probeRequested = false;
      this._probeSession = session;
    }

    // Request light probe once per session
    if (!this._probeRequested && !this._state.lightProbe) {
      this._probeRequested = true;
      this._requestLightProbe(session).catch((err) => {
        Logger.warn('XR', `Failed to request light probe: ${err}`);
      });
      return;
    }

    if (!this._state.lightProbe) return;

    const frame = (globalThis as any).__xrFrame as XRFrame | undefined;
    if (!frame) {
      if (!this._warnedMissingFrame) {
        Logger.warn('XRLightProbe', '__xrFrame missing during active session — App animation loop not populating globalThis.__xrFrame');
        this._warnedMissingFrame = true;
      }
      return;
    }

    // Read light estimate
    try {
      const estimate = frame.getLightEstimate(this._state.lightProbe);
      if (estimate) {
        this._state.lightEstimate = estimate;
        this._updateSceneLight(estimate);
      }
    } catch {
      // Light estimation may not be available for this frame
    }
  }

  private async _requestLightProbe(session: XRSession): Promise<void> {
    try {
      const probe = await session.requestLightProbe({
        reflectionFormat: 'srgba8',
      });
      // The session may have ended while the request was in flight —
      // _handleSessionEnd would have already cleaned up. Bail before creating
      // an orphaned light / probe that nothing will ever dispose.
      if (session !== this._state.session) return;
      this._state.lightProbe = probe;

      // Create managed directional light
      if (!this._state.managedLight) {
        const light = new THREE.DirectionalLight(0xffffff, 1);
        light.name = 'XR_EstimatedLight';
        this._scene.add(light);
        this._state.managedLight = light;
      }

      Logger.debug('XR', 'Light probe created');
    } catch (err) {
      Logger.warn('XR', `Light probe request failed: ${err}`);
      this._probeRequested = false;
    }
  }

  private _updateSceneLight(estimate: XRLightEstimate): void {
    const light = this._state.managedLight;
    if (!light) return;

    // Update primary light direction
    const dir = estimate.primaryLightDirection;
    light.position.set(dir.x, dir.y, dir.z);

    // Update intensity and color from primary light
    const pi = estimate.primaryLightIntensity;
    const maxComponent = Math.max(pi.x, pi.y, pi.z, 0.001);
    light.intensity = maxComponent;
    light.color.setRGB(
      pi.x / maxComponent,
      pi.y / maxComponent,
      pi.z / maxComponent,
    );
  }

  dispose(): void {
    // Defensive: session-end (XRModule._handleSessionEnd) already detaches
    // and disposes this light when a session ends. dispose() may still be
    // invoked during world teardown after a graceful session-end, so we
    // null-check before touching the light to avoid a double-dispose
    // warning from Three.js.
    const light = this._state.managedLight;
    if (light) {
      light.removeFromParent();
      light.dispose();
      this._state.managedLight = null;
    }
    this._state.lightProbe = null;
    this._state.lightEstimate = null;
    this._probeRequested = false;
    this._probeSession = null;
  }
}
